<template>
  <div ref="paginationContainer"
       class="row justify-content-between align-items-center mt-2 mx-0">
    <div ref="perPageColumnWrapper"
         class="col-auto pl-0 pr-1">
      <BFormSelect class="page-options"
                   :size="BootstrapVariant.SMALL"
                   :options="pageOptions"
                   :value="perPage"
                   @input="updatePerPage"></BFormSelect>
    </div>
    <div ref="paginationStatsWrapper"
         class="col-auto p-0">
      {{ paginationStats }}
    </div>
    <div ref="bPaginationWrapper"
         class="col-auto pl-1 pr-0">
      <BPagination class="mr-0"
                   limit="3"
                   :size="BootstrapVariant.SMALL"
                   :hide-ellipsis="true"
                   :per-page="perPage"
                   :total-rows="totalRows"
                   :value="currentPage"
                   @input="updateCurrentPage">
      </BPagination>
    </div>
  </div>
</template>

<script>
  import { Events } from '@ag-grid-community/core';
  import { BPagination, BFormSelect } from 'bootstrap-vue';
  import { translator } from '~/zf/frontend/common/mixins';
  import WindowProvider from '~/zf/frontend/common/window/WindowProvider';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import PerPageOption from '~/zf/frontend/common/ag-grid/pagination/PerPageOption';
  import PortletEvent from '~/zf/frontend/vcom/events/PortletEvent';
  import EventService from '~/zf/frontend/common/event/EventService';

  export default {
    name: 'Pagination',
    compatConfig: {
      MODE: 3,
      COMPONENT_FUNCTIONAL: true,
    },
    components: {
      BPagination,
      BFormSelect,
    },
    mixins: [translator],
    props: {
      initialPerPage: {
        type: Number,
        default: PerPageOption.TWENTY_FIVE,
      },
      portletId: {
        type: String,
      },
      gridApi: {
        required: true,
      },
    },
    emits: ['per-page-changed'],
    data() {
      return {
        BootstrapVariant,
        currentPage: 1,
        pageOptions: [PerPageOption.FIVE, PerPageOption.TEN, PerPageOption.TWENTY_FIVE, PerPageOption.FIFTY, PerPageOption.ONE_HUNDRED],
        paginationStats: '',
        perPage: null,
        totalRows: 0,
      };
    },
    computed: {
      totalPages() {
        return Math.ceil(this.totalRows / this.perPage);
      },
    },
    watch: {
      currentPage() {
        this.updatePaginationData();
      },
      paginationStats() {
        this.$nextTick(this.resize);
      },
      gridApi: {
        immediate: true,
        handler() {
          if (this.gridApi === undefined || this.gridApi === null) {
            return;
          }

          this.gridApi.addEventListener(Events.EVENT_PAGINATION_CHANGED, this.updatePaginationData);
          this.gridApi.addEventListener(Events.EVENT_MODEL_UPDATED, this.updatePaginationData);
        },
      },
    },
    created() {
      if (this.portletId) {
        EventService.addListener(PortletEvent.RESIZED, {
          callback: this.resize,
          target: WindowProvider.document.getElementById(this.portletId),
        });
      }
      this.perPage = this.initialPerPage;
    },
    unmounted() {
      const portletElement = WindowProvider.document.getElementById(this.portletId);
      if (portletElement !== null) {
        EventService.removeListener(PortletEvent.RESIZED, {
          callback: this.resize,
          target: portletElement,
        });
      }
    },
    mounted() {
      this.resize();
    },
    methods: {
      currentStart(currentPage, perPage, totalRows) {
        if (!totalRows) {
          return 0;
        }
        return (currentPage - 1) * perPage + 1;
      },
      currentEnd(currentPage, perPage, totalRows) {
        if (!totalRows) {
          return 0;
        }
        if (totalRows === perPage) {
          return totalRows;
        }
        if (this.totalPages === currentPage) {
          const rest = totalRows % perPage;
          const amountOfRowsForLastPage = rest === 0 ? perPage : rest;
          return (currentPage - 1) * perPage + amountOfRowsForLastPage;
        }
        return currentPage * perPage;
      },
      translateEntries(currentPage, perPage, totalRows) {
        return this.tr('vendor.ag-grid.PAGINATION_STATS')
          .replace('%1$s', this.currentStart(currentPage, perPage, totalRows))
          .replace('%2$s', this.currentEnd(currentPage, perPage, totalRows))
          .replace('%3$s', totalRows);
      },
      updatePaginationData() {
        this.totalRows = this.gridApi.getDisplayedRowCount();
        this.paginationStats = this.translateEntries(
          this.currentPage,
          this.perPage,
          this.totalRows,
        );
      },
      updatePerPage(input) {
        this.perPage = input;
        if (this.gridApi.getGridOption('rowModelType') !== 'serverSide') {
          this.gridApi?.setGridOption('paginationPageSize', Number(input));
        }
        this.$emit('per-page-changed', input);
      },
      updateCurrentPage(selectedPage) {
        this.gridApi.paginationGoToPage(selectedPage - 1);
        this.currentPage = selectedPage;
      },
      resetVisibilityOfPaginationElements() {
        this.showElement(this.$refs.perPageColumnWrapper);
        this.showElement(this.$refs.paginationStatsWrapper);
      },
      resize() {
        this.resetVisibilityOfPaginationElements();
        this.setVisibilityOfPaginationStats();
        this.setVisibilityOfPerPage();
      },
      isRoomForPaginationStats() {
        const remainingSpaceForPaginationStats = this.$refs.paginationContainer.clientWidth
          - this.outerWidthIncludingMargin(this.$refs.perPageColumnWrapper)
          - this.outerWidthIncludingMargin(this.$refs.bPaginationWrapper);
        return remainingSpaceForPaginationStats > this.outerWidthIncludingMargin(this.$refs.paginationStatsWrapper);
      },
      setVisibilityOfPaginationStats() {
        if (!this.isRoomForPaginationStats()) {
          this.hideElement(this.$refs.paginationStatsWrapper);
        }
      },
      isRoomForPerPage() {
        const remainingSpaceForPerPage = this.$refs.paginationContainer.clientWidth
          - this.outerWidthIncludingMargin(this.$refs.paginationStatsWrapper)
          - this.outerWidthIncludingMargin(this.$refs.bPaginationWrapper);
        return remainingSpaceForPerPage > this.outerWidthIncludingMargin(this.$refs.perPageColumnWrapper);
      },
      setVisibilityOfPerPage() {
        if (!this.isRoomForPerPage()) {
          this.hideElement(this.$refs.perPageColumnWrapper);
        }
      },
      hideElement(element) {
        element.classList.add('d-none');
      },
      showElement(element) {
        element.classList.remove('d-none');
      },
      outerWidthIncludingMargin(element) {
        const { style } = element;
        const marginLeft = parseInt(style.marginLeft, 10) || 0;
        const marginRight = parseInt(style.marginRight, 10) || 0;
        return marginLeft + element.offsetWidth + marginRight;
      },
    },
  };
</script>
